import type { AssignableRole } from "@unbox/api";
import { ChevronDown } from "@untitledui/icons";
import {
  Button as AriaButton,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  Popover as AriaPopover,
  Select as AriaSelect,
} from "react-aria-components";
import {
  AssignableRoleLabels,
  RoleOption,
  type ScopedRoleValue,
  scopedRoleDescriptions,
  scopedRoleLabels,
} from "@/components/admin/role-label";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

// The admin console's `role-label` / `role-select` pills are hard-coded to the
// dark console theme, so Settings needs its own light styling off the shared
// utility tokens. Labels still come from `AssignableRoleLabels`.

/**
 * Shown when the viewer lacks the authority to hand out a role. The control stays
 * visible and disabled behind this tooltip instead of disappearing, so a
 * permission limit doesn't read as a missing feature (UNB-2129).
 */
export const TEAM_ROLE_ASSIGN_DENIED = {
  title: "You do not have permissions to assign this role",
  description: 'Please ask a colleague who has the role "Merchant Admin" if you would like to assign this role.',
};

const rolePill: Record<ScopedRoleValue, string> = {
  user: "bg-utility-brand-50 text-utility-brand-700 ring-utility-brand-200",
  merchant_admin: "bg-utility-blue-50 text-utility-blue-700 ring-utility-blue-200",
  // Not in the merchant design, which only covers the two brand-scoped roles.
  workspace_admin: "bg-utility-indigo-50 text-utility-indigo-700 ring-utility-indigo-200",
  // Never selectable: picking a role is what resolves it.
  multiple: "bg-utility-gray-50 text-utility-gray-700 ring-utility-gray-200",
};

const pillBase = "inline-flex items-center rounded-md py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset";

export const TeamRoleBadge = ({ role }: { role: ScopedRoleValue }) => (
  <Tooltip title={scopedRoleDescriptions[role]}>
    <TooltipTrigger className="inline-flex cursor-default">
      <span className={cx(pillBase, "px-1.5", rolePill[role])}>{scopedRoleLabels[role]}</span>
    </TooltipTrigger>
  </Tooltip>
);

type TeamRoleSelectProps = {
  /** "multiple" renders as a label only — the options are always concrete roles. */
  value: ScopedRoleValue;
  onChange: (role: AssignableRole) => void;
  /** Roles this scope offers. A single brand offers Merchant Admin / User. */
  options: AssignableRole[];
  /** Roles the viewer may actually grant — `usePermissions().invitableRoles`. */
  assignableRoles: AssignableRole[];
  isDisabled?: boolean;
  "aria-label": string;
};

/**
 * The role pill dropdown. Options the viewer can't grant stay listed but
 * unselectable, and each explains why on hover of its option in the open menu.
 */
export const TeamRoleSelect = ({
  value,
  onChange,
  options,
  assignableRoles,
  isDisabled,
  "aria-label": ariaLabel,
}: TeamRoleSelectProps) => {
  const blocked = options.filter((role) => !assignableRoles.includes(role));

  const trigger = (
    <AriaButton
      className={cx(
        "flex items-center gap-0.5 pl-2 pr-1.5 outline-hidden transition-colors",
        pillBase,
        rolePill[value],
        isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
      )}
    >
      <span>{scopedRoleLabels[value]}</span>
      <ChevronDown aria-hidden="true" className="size-3 shrink-0" />
    </AriaButton>
  );

  return (
    <AriaSelect
      aria-label={ariaLabel}
      selectedKey={value === "multiple" ? null : value}
      isDisabled={isDisabled}
      // Must live on the Select, not the ListBox: the Select owns the collection
      // state, so disabledKeys on the inner ListBox is ignored.
      disabledKeys={blocked}
      onSelectionChange={(key) => {
        if (key) onChange(key as AssignableRole);
      }}
      className="w-fit"
    >
      {trigger}

      <AriaPopover
        offset={4}
        placement="bottom end"
        className="pointer-events-auto w-80 overflow-auto rounded-lg bg-primary py-1 shadow-lg ring-1 ring-secondary_alt outline-hidden"
      >
        <AriaListBox className="outline-hidden">
          {options.map((role) => (
            <AriaListBoxItem
              key={role}
              id={role}
              textValue={AssignableRoleLabels[role]}
              className={({ isFocused, isSelected, isDisabled: itemDisabled }) =>
                cx(
                  "flex items-start gap-2 px-3 py-2 outline-hidden",
                  itemDisabled ? "cursor-not-allowed" : "cursor-pointer",
                  isFocused && "bg-primary_hover",
                  isSelected && "bg-active",
                )
              }
            >
              {({ isSelected, isDisabled: itemDisabled }) =>
                itemDisabled ? (
                  // The option carries the "can't assign" explanation now, not the pill
                  // trigger. It's disabled in the listbox, so the focusable trigger the
                  // tooltip needs to anchor to never takes selection or a tab stop.
                  <Tooltip {...TEAM_ROLE_ASSIGN_DENIED} placement="left">
                    <TooltipTrigger
                      excludeFromTabOrder
                      className="inline-flex w-full items-start gap-2 cursor-not-allowed text-left"
                    >
                      <RoleOption role={role} isSelected={isSelected} isDisabled={itemDisabled} />
                    </TooltipTrigger>
                  </Tooltip>
                ) : (
                  <RoleOption role={role} isSelected={isSelected} isDisabled={itemDisabled} />
                )
              }
            </AriaListBoxItem>
          ))}
        </AriaListBox>
      </AriaPopover>
    </AriaSelect>
  );
};
